import { Button } from "@/components/ui/button";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";

/** Bildausschnitt: Voreinstellung + Feinjustierung per Pfeilen (5 %-Schritte). Wert = "x% y%". */
export const parsePosition = (v) => {
  const presets = { top: [50, 0], center: [50, 50], bottom: [50, 100] };
  if (!v) return [50, 50];
  if (presets[v]) return presets[v];
  const m = String(v).match(/(\d+)%\s+(\d+)%/);
  return m ? [Number(m[1]), Number(m[2])] : [50, 50];
};
export const positionCss = (v) => { const [x, y] = parsePosition(v); return `${x}% ${y}%`; };

/** Vorschau-Formate wie auf der Startseite: hero (breit), large (Teneriffa-Kachel), portrait (4:5), landscape (16:10). */
const FORMATS = {
  hero: { ratio: null, height: 64, title: "Vorschau im Hero-Format" },
  "hero-home": { ratio: null, height: 136, title: "Vorschau: Titelbild (wie auf der Startseite)" },
  large: { ratio: "4 / 5", height: 150, title: "Vorschau: große Kachel (wie auf der Startseite)" },
  portrait: { ratio: "4 / 5", height: 120, title: "Vorschau: Hochformat-Kachel (wie auf der Startseite)" },
  landscape: { ratio: "16 / 10", height: 96, title: "Vorschau: Querformat-Kachel (wie auf der Startseite)" },
};

const TilePreview = ({ image, x, y, format, label, hero, testId }) => {
  const f = FORMATS[format] || FORMATS.hero;
  return (
    <div className={`relative overflow-hidden rounded-lg border border-stone-200 bg-stone-100 shrink-0 ${f.ratio ? "" : "flex-1 min-w-0"}`} style={{ height: f.height, aspectRatio: f.ratio || undefined }} title={f.title} data-testid={testId}>
      <img src={image} alt="" className="w-full h-full object-cover" style={{ objectPosition: `${x}% ${y}%` }} />
      {hero && (
        <>
          <div className="absolute inset-0 hero-gradient-overlay" />
          <div className="absolute inset-x-0 bottom-0 px-3 pb-2 text-white leading-tight">
            <div className="text-[5px] uppercase tracking-[0.3em] text-white/90 font-semibold mb-1">{hero.badge}</div>
            <div className="font-serif text-[11px]">{hero.title}</div>
            <div className="font-serif italic text-[#f0b400] text-[9px]">{hero.accent}</div>
            <div className="mt-1.5 h-3 w-3/4 rounded bg-white/90" />
          </div>
        </>
      )}
      {label && (
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute bottom-1.5 left-2 text-white leading-tight">
            <div className="text-[6px] uppercase tracking-widest text-[#d9a63a]">Insel</div>
            <div className="font-serif text-[11px]">{label}</div>
          </div>
        </>
      )}
    </div>
  );
};

export const ImagePositionPicker = ({ value, onChange, image, testId = "img-pos", compact = false, format = "hero", previewLabel, heroText }) => {
  const [x, y] = parsePosition(value);
  const set = (nx, ny) => onChange(`${Math.min(100, Math.max(0, nx))}% ${Math.min(100, Math.max(0, ny))}%`);
  const preset = (p) => { const [px, py] = parsePosition(p); set(px, py); };
  const isPreset = (p) => { const [px, py] = parsePosition(p); return px === x && py === y; };
  const btn = compact ? "h-6 w-6 px-0" : "h-8 px-0";
  const icon = compact ? "w-3 h-3" : "w-4 h-4";
  const arrows = (
    <div className={`grid grid-cols-3 gap-1 ${compact ? "w-[80px]" : "w-[104px]"}`}>
      <span /><Button type="button" size="sm" variant="outline" className={btn} onClick={() => set(x, y - 5)} title="Bild nach oben (zeigt mehr vom unteren Teil)" data-testid={`${testId}-up`}><ArrowUp className={icon} /></Button><span />
      <Button type="button" size="sm" variant="outline" className={btn} onClick={() => set(x - 5, y)} data-testid={`${testId}-left`}><ArrowLeft className={icon} /></Button>
      <div className={`${compact ? "h-6" : "h-8"} flex items-center justify-center text-[10px] text-stone-500 tabular-nums`} data-testid={`${testId}-value`}>{x}/{y}</div>
      <Button type="button" size="sm" variant="outline" className={btn} onClick={() => set(x + 5, y)} data-testid={`${testId}-right`}><ArrowRight className={icon} /></Button>
      <span /><Button type="button" size="sm" variant="outline" className={btn} onClick={() => set(x, y + 5)} data-testid={`${testId}-down`}><ArrowDown className={icon} /></Button><span />
    </div>
  );
  if (compact) {
    return (
      <div className="flex items-center gap-3" data-testid={testId}>
        <div className="space-y-1">
          <div className="text-[11px] text-stone-500">Bildausschnitt</div>
          {arrows}
        </div>
        {image && <TilePreview image={image} x={x} y={y} format={format} label={previewLabel} hero={heroText} testId={`${testId}-preview`} />}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-start gap-4" data-testid={testId}>
      <div className="space-y-2">
        <div className="text-xs text-stone-500">Bildausschnitt</div>
        <div className="flex gap-1.5">
          {[["top", "Oben"], ["center", "Mitte"], ["bottom", "Unten"]].map(([p, l]) => (
            <Button key={p} type="button" size="sm" variant={isPreset(p) ? "default" : "outline"} onClick={() => preset(p)} className={isPreset(p) ? "bg-[#2d6b0d] hover:bg-[#1e4a08]" : ""} data-testid={`${testId}-${p}`}>{l}</Button>
          ))}
        </div>
        {arrows}
        <p className="text-[11px] text-stone-400 max-w-[220px]">Pfeile verschieben den sichtbaren Ausschnitt in 5 %-Schritten. Vorschau rechts zeigt das Hero-Format.</p>
      </div>
      {image && (
        <div className="flex-1 min-w-[320px] rounded-xl overflow-hidden border border-stone-200 bg-stone-100" style={{ aspectRatio: "16 / 5", maxHeight: 300 }} title="Vorschau im Hero-Format (wie auf der Seite)" data-testid={`${testId}-preview`}>
          <img src={image} alt="" className="w-full h-full object-cover" style={{ objectPosition: `${x}% ${y}%` }} />
        </div>
      )}
    </div>
  );
};
